import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { partner } from '../api/partner';
import { toast } from '../app/toast';
import { flushPending } from '../features/steps/stepFlow';
import { usePending, waitingCount } from '../features/steps/pending';
import { useT, type DictKey } from '../i18n';
import { CaseIcon, HandoverIcon, HomeIcon, MoreIcon, TruckIcon } from './Icons';
import { ErrorBoundary } from './ErrorBoundary';
import { ProblemButton } from './ProblemButton';

const TABS: { to: string; key: DictKey; icon: ReactNode; end?: boolean }[] = [
  { to: '/', key: 'nav.home', icon: <HomeIcon />, end: true },
  { to: '/handovers', key: 'nav.handovers', icon: <HandoverIcon /> },
  { to: '/cases', key: 'nav.cases', icon: <CaseIcon /> },
  { to: '/trips', key: 'nav.trips', icon: <TruckIcon /> },
  { to: '/more', key: 'nav.more', icon: <MoreIcon /> },
];

function useOnline() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** Steps saved offline: banner + automatic send when the network comes back (and on start). */
function PendingBanner({ online }: { online: boolean }) {
  const t = useT();
  const n = usePending(waitingCount);
  const [busy, setBusy] = useState(false);
  const flush = async (quiet: boolean) => {
    setBusy(true);
    try {
      const sent = await flushPending(partner.addStep);
      if (sent && !quiet) toast(t('step.saved'));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (online && waitingCount(usePending.getState())) void flush(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);
  if (!n) return null;
  return (
    <div className="page mt-3">
      <div className="card flex items-center gap-3 border-warn/60">
        <p className="min-w-0 flex-1 text-[15px] text-warn">{t(online ? 'step.pendingOnline' : 'step.pendingOffline', { n })}</p>
        <button type="button" className="btn" disabled={busy || !online} onClick={() => void flush(false)}>
          {t('step.sendNow')}
        </button>
      </div>
    </div>
  );
}

/** Authenticated shell: content, floating «Проблема / ідея», bottom tab bar (thumb reach). */
export function Layout({ children }: { children: ReactNode }) {
  const t = useT();
  const online = useOnline();
  const { pathname } = useLocation();
  return (
    <div className="min-h-[100dvh]" style={{ paddingBottom: 'calc(var(--tabbar-h) + var(--problem-h) + env(safe-area-inset-bottom, 0px) + 16px)' }}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:bg-card focus:p-3">
        {t('app.skip')}
      </a>
      {!online ? (
        <div role="status" className="bg-warn px-4 py-2 text-center text-[14px] font-medium text-white">
          {t('app.offline')}
        </div>
      ) : null}
      <PendingBanner online={online} />
      <main id="main" tabIndex={-1} className="page outline-none">
        <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
      </main>
      <ProblemButton />
      <nav
        aria-label={t('nav.main')}
        className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-line bg-card"
      >
        <ul className="mx-auto grid max-w-xl grid-cols-5" style={{ height: 'var(--tabbar-h)' }}>
          {TABS.map((tab) => (
            <li key={tab.to} className="min-w-0">
              <NavLink
                to={tab.to}
                end={tab.end}
                aria-label={t(tab.key)}
                className={({ isActive }) =>
                  `flex h-full min-h-tap flex-col items-center justify-center gap-0.5 px-1 text-[12px] leading-tight ${isActive ? 'font-semibold text-fg' : 'text-lab'}`
                }
              >
                {tab.icon}
                <span className="block max-w-full truncate">{t(tab.key)}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
